import React from "react";

import {
  Building2,
  Eye,
  Pencil,
  Play,
  Calendar,
  AlertCircle,
} from "lucide-react";

import Button from "../../Components/Button";
import CadenceTag from "../../Components/CadenceTag";
import { assessmentCategoryLabels } from "../../Utils/Constants/Constants";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";
import { isMyAssessmentsSelfOrigin } from "../../Utils/Helper/assessmentOriginHelpers";

type ReviewAssessmentCardProps = {
  card: any;
  onAction?: (card: any, options?: { mode?: "view" | "edit" }) => void;
};

const ActionIcon: React.FC = () => <Play className="w-4 h-4" size={16} />;

const MetaItem = ({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-1.5 whitespace-nowrap ${className}`}>
    <span className="text-xs font-medium text-gray-500 shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-700">{value}</span>
  </div>
);

const ReviewAssessmentCard: React.FC<ReviewAssessmentCardProps> = ({
  card,
  onAction,
}) => {
  const { formatTier } = useAccountTiers();
  const isAbortedCard = card.contextTab === "aborted";
  const isPublishedCard = card.contextTab === "published";
  const isCompletedCard =
    card.contextTab === "completed" ||
    card.contextTab === "aborted" ||
    card.contextTab === "published";
  const accountTierLabel =
    formatTier(card.accountTier) || card.accountTier || "";
  const showDueDate = card.contextTab !== "draft";
  const isTeamAssessment = isMyAssessmentsSelfOrigin(
    { assessment_type: card.assessmentType, notes: card.notes },
    card.contextTab
  );
  const assignedByMetaLabel =
    isTeamAssessment &&
    (card.contextTab === "assigned" || card.contextTab === "completed")
      ? "Trigger By:"
      : "Assigned By:";

  // Get category-specific styling
  const getCategoryColors = (category: string) => {
    switch (category?.toLowerCase()) {
      case "short":
      case "engagement":
        return "bg-blue-50 text-blue-700 border border-blue-200";
      case "comprehensive":
        return "bg-purple-50 text-purple-700 border border-purple-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  return (
    <div
      className={`group relative border rounded-lg p-5 bg-white flex flex-col gap-4 justify-between transition-all duration-200 hover:shadow-md ${
        isAbortedCard
          ? "border-red-200"
          : isCompletedCard
            ? "border-gray-300"
            : "border-gray-200 hover:border-gray-300"
      }`}
    >
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="m-0 text-[17px] font-semibold leading-[1.4] break-words text-gray-900">
              {card.assessmentName}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isAbortedCard || isPublishedCard ? (
              <span
                title={
                  isTeamAssessment ? "Team assessment" : "Formal assessment"
                }
                className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide uppercase ${
                  isTeamAssessment
                    ? "bg-teal-50 text-teal-700 border border-teal-200"
                    : "bg-indigo-50 text-indigo-700 border border-indigo-200"
                }`}
              >
                {isTeamAssessment ? "T" : "F"}
              </span>
            ) : null}
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide uppercase ${getCategoryColors(
                card.questionnaireLabel
              )}`}
            >
              {assessmentCategoryLabels[
                card.questionnaireLabel as keyof typeof assessmentCategoryLabels
              ] || card.questionnaireLabel}
            </span>
          </div>
        </div>

        {/* Description and Due Date Row */}
        {card.description || (showDueDate && card.dueDateLabel) ? (
          <div className="flex items-start justify-between gap-4">
            {card.description ? (
              <p className="m-0 text-gray-600 text-sm leading-[1.6] flex-1">
                {card.description}
              </p>
            ) : (
              <span className="invisible max-w-0 p-0 m-0" />
            )}
            {showDueDate && card.dueDateLabel && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {card.isHeld && (
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                    title="Your input was given during a disconnect"
                  >
                    Held · counts after the disconnect
                  </span>
                )}
                {card.isOverdue && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-orange-50 text-orange-400 border border-orange-100">
                    <AlertCircle className="w-3 h-3" />
                    Overdue
                  </span>
                )}
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${
                    card.isOverdue
                      ? "bg-orange-50 border-orange-100"
                      : "bg-gray-50 border-gray-300"
                  }`}
                >
                  <Calendar
                    className={`w-3.5 h-3.5 ${
                      card.isOverdue ? "text-orange-400" : "text-gray-500"
                    }`}
                  />
                  <span className="text-xs font-medium text-gray-600">
                    Due:
                  </span>
                  <span
                    className={`text-xs font-medium whitespace-nowrap ${
                      card.isOverdue ? "text-orange-400" : "text-gray-900"
                    }`}
                  >
                    {card.dueDateLabel}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-200" />

      {/* Footer Section */}
      <div className="space-y-3">
        {/* Account Info */}
        <div className="flex items-center justify-between gap-3 rounded-lg py-2.5 px-3 bg-gray-50 border border-gray-200">
          <div className="inline-flex items-center gap-2.5 flex-1 min-w-0">
            <Building2
              className="w-4 h-4 text-gray-500"
              size={16}
              aria-label="Account"
            />
            <span className="font-medium text-gray-900 text-sm truncate">
              {card.accountName}
            </span>
            {accountTierLabel && (
              <span
                className={`py-0.5 px-2 rounded-md text-[10px] font-semibold tracking-wide border ${getTierBadgeClasses(
                  card.accountTier
                )}`}
              >
                {accountTierLabel}
              </span>
            )}
            {card.cadenceTag ? <CadenceTag tag={card.cadenceTag} /> : null}
          </div>
        </div>

        {/* Meta Info and Action — original side-by-side footer */}
        <div className="flex items-end justify-between gap-3">
          <div className="flex flex-col gap-1.5 text-xs text-gray-600 min-w-0">
            {isCompletedCard ? (
              <>
                <MetaItem
                  label={
                    isAbortedCard
                      ? "Aborted On:"
                      : isPublishedCard
                        ? "Published On:"
                        : "Completed On:"
                  }
                  value={
                    isAbortedCard
                      ? card.abortAt
                      : (card.completedOnLabel ?? card.dueDateLabel)
                  }
                />
                {isAbortedCard ? (
                  <MetaItem label="Abort Reason:" value={card.abortReason} />
                ) : (
                  <MetaItem
                    label={assignedByMetaLabel}
                    value={card.assignedByLabel}
                  />
                )}
              </>
            ) : (
              <>
                <MetaItem label="Assigned On:" value={card.assignedOnLabel} />
                <MetaItem
                  label={assignedByMetaLabel}
                  value={card.assignedByLabel}
                />
              </>
            )}
          </div>

          <div className="flex justify-end flex-shrink-0 gap-2">
            {card.showEditButton ? (
              <Button
                variant="outline-primary"
                className="px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap"
                onClick={() => onAction?.(card, { mode: "edit" })}
              >
                <Pencil className="w-4 h-4 shrink-0" size={16} />
                <span>Re-assess</span>
              </Button>
            ) : null}
            {card.actionLabel ? (
              <Button
                variant="primary"
                className={`px-3 py-2 rounded-lg inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  isCompletedCard
                    ? "bg-indigo-600 hover:bg-indigo-700 text-white border-0"
                    : "bg-blue-600 hover:bg-blue-700 text-white border-0"
                }`}
                onClick={() => onAction?.(card, { mode: "view" })}
              >
                {isCompletedCard ? (
                  <Eye className="w-4 h-4 shrink-0" size={16} />
                ) : (
                  <ActionIcon />
                )}
                <span>{card.actionLabel}</span>
              </Button>
            ) : isCompletedCard ? (
              <span className="text-xs font-medium text-gray-500 italic">
                No response submitted
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewAssessmentCard;
